import { FC, useEffect, useMemo, useState } from 'react';

import { CountryUtils } from '@ravago/shared/next-data-access/client';
import { AccountInformationFormValue, RegistrationStep } from '@ravago/shared/page-data/models/elements';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Dropdown, DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { PhoneInputField } from '@ravago/shared/radiance/components/input-field/components/PhoneInputField/PhoneInputField';
import { Countries, CountryCode } from '@ravago/shared/radiance/components/input-field/components/PhoneInputField/PhoneInputField.types';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { isValidPhoneNumber } from 'libphonenumber-js';
import { Controller, useForm } from 'react-hook-form';

import { getCountryCode } from '../../../../utils/locale/locale.utils';

interface AccountInformationStepProps {
   showRegistrationAlert: boolean;
   locale: string;
   defaultFormValue: AccountInformationFormValue | undefined;
   handleSubmitAccountInfoForm: (formValue: AccountInformationFormValue) => void;
   handleChangeStep: (step: RegistrationStep) => void;
}

const getDefaultFormValue = (defaultCountry: string, countryOptions: DropdownOption[]) => {
   const defaultCountryOption = countryOptions.find((countryOption) => countryOption.id === defaultCountry);
   const defaultPhoneCountry = defaultCountryOption ? Countries[defaultCountryOption.id as CountryCode] : undefined;
   const defaultPhone = defaultPhoneCountry ? { phone: '', countryInfo: defaultPhoneCountry } : undefined;

   return {
      firstName: '',
      lastName: '',
      companyName: '',
      country: defaultCountryOption,
      phone: defaultPhone,
      acceptTermsAndConditions: false
   };
};

export const AccountInformationStep: FC<AccountInformationStepProps> = ({
   showRegistrationAlert,
   locale,
   defaultFormValue,
   handleSubmitAccountInfoForm,
   handleChangeStep
}) => {
   const translate = useTranslations();

   const [countries, setCountries] = useState<{ shortName: string; fullName: string }[] | undefined>([]);

   useEffect(() => {
      const fetchCountries = async () => {
         const result = await CountryUtils.getCountries();
         setCountries(result ?? []);
      };
      fetchCountries();
   }, []);

   const countryOptions: DropdownOption[] = useMemo(
      () =>
         (countries ?? [])
            .map((country) => ({
               id: country.shortName,
               value: country.fullName
            }))
            .sort((a, b) => a.value.localeCompare(b.value)),
      [countries]
   );

   const { control, handleSubmit, setValue, getValues } = useForm<AccountInformationFormValue, unknown, AccountInformationFormValue>({
      defaultValues: defaultFormValue ?? getDefaultFormValue(getCountryCode(locale), countryOptions)
   });

   const onCountryChanged = (newValue: DropdownOption | undefined) => {
      if (newValue) {
         const phone = getValues('phone');
         const countryInfo = Countries[newValue.id as CountryCode];
         setValue('phone', { phone: phone?.phone ?? '', countryInfo });
      }
   };

   const handleSubmitForm = async () => {
      await handleSubmit((value) => {
         handleSubmitAccountInfoForm(value);
      })();
   };

   return (
      <Box direction="column" gap="lg">
         {showRegistrationAlert && (
            <Alert headline={translate('auth.registration-alert-title')} variant="neutral">
               {translate('auth.registration-alert-description')} <Link href="mailto:support@ultrapolymers.com">support@ultrapolymers.com</Link>.
            </Alert>
         )}
         <Grid>
            <GridCol spanDesktop={6} spanTablet={4} spanPhone={4}>
               <Controller
                  control={control}
                  name="firstName"
                  rules={{
                     required: translate('form-validation.required', { field: translate('auth.first-name') }),
                     maxLength: {
                        value: 40,
                        message: translate('form-validation.max-length', { field: translate('auth.first-name') })
                     }
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <InputField
                        inputRef={ref}
                        {...field}
                        id="registration-first-name"
                        label={translate('auth.first-name')}
                        required
                        type="text"
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />
            </GridCol>
            <GridCol spanDesktop={6} spanTablet={4} spanPhone={4}>
               <Controller
                  control={control}
                  name="lastName"
                  rules={{
                     required: translate('form-validation.required', { field: translate('auth.last-name') }),
                     maxLength: {
                        value: 40,
                        message: translate('form-validation.max-length', { field: translate('auth.last-name') })
                     }
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <InputField
                        inputRef={ref}
                        {...field}
                        id="registration-last-name"
                        label={translate('auth.last-name')}
                        required
                        type="text"
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />
            </GridCol>
         </Grid>
         <GridCol spanDesktop={6} spanTablet={5} spanPhone={4}>
            <Controller
               control={control}
               name="companyName"
               rules={{
                  required: translate('form-validation.required', { field: translate('auth.company-name') }),
                  maxLength: {
                     value: 40,
                     message: translate('form-validation.max-length', { field: translate('auth.company-name') })
                  }
               }}
               render={({ field: { ref, ...field }, fieldState: { error } }) => (
                  <InputField
                     inputRef={ref}
                     {...field}
                     id="registration-company-name"
                     label={translate('auth.company-name')}
                     required
                     type="text"
                     error={!!error}
                     errorMessage={error?.message}
                  />
               )}
            />
         </GridCol>

         <Grid>
            <GridCol spanDesktop={6} spanTablet={4} spanPhone={4}>
               <Controller
                  control={control}
                  name="country"
                  rules={{
                     required: translate('form-validation.required', { field: translate('auth.country') })
                  }}
                  render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
                     <Dropdown
                        inputRef={ref}
                        {...field}
                        onChange={(e) => {
                           onChange(e);
                           onCountryChanged(e);
                        }}
                        id="dropdown-country"
                        label={translate('auth.country')}
                        leadingIcon=""
                        emptyOption={false}
                        options={countryOptions}
                        disabled={!countries}
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />
            </GridCol>
            <GridCol spanDesktop={6} spanTablet={4} spanPhone={4}>
               <Controller
                  control={control}
                  name="phone"
                  rules={{
                     validate: {
                        valid: (value) =>
                           value && isValidPhoneNumber(`+${value.countryInfo?.prefix}${value.phone}`)
                              ? undefined
                              : translate('auth.invalid-phone-number')
                     }
                  }}
                  render={({ field: { ref, ...field }, fieldState: { error } }) => (
                     <PhoneInputField
                        inputRef={ref}
                        {...field}
                        autocomplete="on"
                        countryFilter={[]}
                        filterType="include"
                        id="company-phone"
                        label={translate('auth.phone')}
                        error={!!error}
                        errorMessage={error?.message}
                     />
                  )}
               />
            </GridCol>
         </Grid>

         <Grid>
            <GridCol spanDesktop={6} spanTablet={6} spanPhone={4}>
               <Box items="center" gap="2xs">
                  <Controller
                     control={control}
                     name="acceptTermsAndConditions"
                     rules={{
                        validate: (currentValue) => currentValue === true
                     }}
                     render={({ field: { ref, value, ...field }, fieldState: { error } }) => (
                        <Checkbox inputRef={ref} checked={value} {...field} error={!!error} />
                     )}
                  />
                  <Typography>
                     {translate('auth.i-agree-to-the')}
                     &nbsp;
                     {/* TODO: make this link configurable through the component  */}
                     <Link href={`/${locale}/terms-of-use`} target="_blank">
                        {translate('auth.terms-and-conditions')}
                     </Link>
                  </Typography>
               </Box>
            </GridCol>
         </Grid>

         <Box justify="between">
            <Button variant="tertiary" leadingIcon="arrow-left" onClick={() => handleChangeStep(RegistrationStep.EMAIL_ADDRESS)}>
               {translate('auth.previous-step')}
            </Button>

            <Button onClick={() => handleSubmitForm()}>
               {translate('auth.send-registration')}
               <Icon icon="paper-plane" />
            </Button>
         </Box>
      </Box>
   );
};
